import type { Currency } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';

export enum NavigationTabs {
  overview = 'overview',
  thePath = 'thePath',
  thePasses = 'thePasses',
  more = 'more'
}

export type NavigationTabTypes = keyof typeof NavigationTabs;

export enum ModalRoutes {
  RedeemPassWizard = '/powerpath/redeem-pass-flow',
  PassBundle = '/powerpath/pass-bundle',
  HowItWorks = '/powerpath/how-it-works',
  MobileSidecarScreen = '/powerpath/sidecar-screen'
}

export type PPDataType = 'real' | 'mock';

export enum StepKeyName {
  step1 = 'step1',
  step2SNG = 'step2SNG',
  step2Scheduled = 'step2Scheduled',
  step3 = 'step3',
  step4 = 'step4'
}

export enum StepKeyValue {
  step1 = 1,
  step2SNG = 2,
  step2Scheduled = 2,
  step3 = 3,
  step4 = 4
}

export type StepKey = keyof typeof StepKeyName;

export enum PowerPathType {
  SPIN_AND_GO = 'SPIN_AND_GO',
  SIT_AND_GO = 'SIT_AND_GO',
  SCHEDULED = 'SCHEDULED'
}

export interface RawStep {
  type: PowerPathType;
  id: number;
  step: number;
  currency: Currency;
  buyIn: number;
  formattedBuyIn: number;
  fee: number;
  startTime: number | null;
  tournFlagsServInt: number;
  maxPlayers: number;
  minPlayers: number;
  whenStart: number | null;
  whenStart_t: number;
  regEnd: number | null;
  regEnd_t: number;
  enrolled: number | null;
  prize: number;
  legacyStatus: number;
  prizePoolValue: number;
  prizePoolUnit: number;
  prizePool: number;
  restricted: boolean;
}

export type Step = Omit<RawStep, 'whenStart'> & {
  key: StepKey;
  whenStartInt: number;
  regEndInt: number;
  whenStart: string;
};

export type Steps = {
  [K in StepKey]: Step | null;
};

export interface DataResponse {
  data: RawStep[];
}

export enum PassType {
  gold = 'gold',
  silver = 'silver',
  bronze = 'bronze'
}

export type PassTypeKey = keyof typeof PassType;

export enum PassRedemption {
  Online = 'online',
  Live = 'live'
}
export type PassRedemptionType = `${PassRedemption}`;

export enum Award {
  Ticket = 'ticket',
  Entry = 'entry',
  Hotel = 'hotel',
  Addon = 'addon',
  Other = 'other'
}

export type AwardType = `${Award}`;

export enum Event {
  Series = 'series',
  Festival = 'festival',
  Recurring = 'recurring'
}

export type EventType = `${Event}`;

export interface PassEvent {
  infoUrl: string;
  startDate: string;
  endDate: string;
  eventType: EventType;
}

export interface PassAward {
  params: string[];
  type: AwardType;
  description: string;
  _id?: string;
}

export interface Pass {
  name: string;
  type: PassTypeKey;
  value: string;
  redemptionType: PassRedemptionType;
  image: string;
  tournId: string;
  targeting: string;
  order: number;
  event: PassEvent;
  awards: PassAward[];
}

export type PassBundleRaw = {
  [K in PassTypeKey]: Pass[];
};

export type PassBundle = PassBundleRaw | null;

export interface NavigationTabUrl {
  overview: string;
  'the-path': string;
  'the-passes': string;
  more: string;
}

type NavigationTabUrlKey = keyof NavigationTabUrl;

export const NAVIGATION_TABS_TYPE_URL_MAP: { [key in NavigationTabs]: NavigationTabUrlKey } = {
  [NavigationTabs.overview]: 'overview',
  [NavigationTabs.thePath]: 'the-path',
  [NavigationTabs.thePasses]: 'the-passes',
  [NavigationTabs.more]: 'more'
};

export const NAVIGATION_TABS_URL_TYPE_MAP: { [key in NavigationTabUrlKey]: NavigationTabs } = {
  overview: NavigationTabs.overview,
  'the-path': NavigationTabs.thePath,
  'the-passes': NavigationTabs.thePasses,
  more: NavigationTabs.more
};

interface PluralizedTranslation {
  SINGULAR: TranslationTag;
  PLURAL: TranslationTag;
}

export interface PassAwardTranslations {
  [Award.Ticket]: PluralizedTranslation;
  [Award.Entry]: PluralizedTranslation;
  [Award.Hotel]: TranslationTag;
  [Award.Addon]: TranslationTag;
}
